import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CipherMapping } from "@/lib/types/cipher";

interface MappingReferencePanelProps {
  characters: string[];
  mapping: CipherMapping;
}

export default function MappingReferencePanel({
  characters,
  mapping,
}: MappingReferencePanelProps) {
  // Calculate completion
  const mappedCount = Object.keys(mapping.forward).length;
  const totalCount = characters.length;
  const progress = totalCount > 0 ? (mappedCount / totalCount) * 100 : 0;
  const isComplete = mappedCount === totalCount;

  return (
    <div className="space-y-3">
      {/* Progress */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">Mapping Progress</h4>
          <Badge variant={isComplete ? "default" : "secondary"} className="text-xs">
            {mappedCount} / {totalCount}
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />
        {isComplete ? (
          <p className="text-xs text-green-600 font-medium">✓ All characters mapped!</p>
        ) : (
          <p className="text-xs text-gray-500">
            {totalCount - mappedCount} character{totalCount - mappedCount !== 1 ? "s" : ""} remaining
          </p>
        )}
      </div>

      {/* Mapping Table */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Character → Emoji Mappings</h4>
        <ScrollArea className="h-full">
          {characters.length > 0 ? (
            <div className="space-y-0.5">
              {characters.map((char) => {
                const emoji = mapping.forward[char];
                const isMapped = !!emoji;

                return (
                  <div
                    key={char}
                    className={`flex items-center justify-between px-2 py-1 rounded transition-colors ${
                      isMapped
                        ? "bg-white border border-gray-200"
                        : "bg-gray-100 border border-dashed border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium w-6 text-center">
                        {char === " " ? "␣" : char}
                      </span>
                      <span className="text-gray-400 text-xs">→</span>
                      <span className="text-lg">
                        {isMapped ? emoji.targetEmoji : "❓"}
                      </span>
                    </div>
                    {!isMapped && (
                      <Badge variant="outline" className="text-xs">
                        Not mapped
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">
              No characters to map
            </p>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

